import { createSlice } from "@reduxjs/toolkit";
import undoable, { excludeAction } from "redux-undo";
import {
  BaseSrcIntDestPlateInfo,
  LabwareTypeInfo,
  WorklistValues,
} from "../shared/interfaces";
import {
  getCompoundPlateInfoState,
  getIntermediatePlateInfoState,
  getNucleofectionPlateInfoState,
  getSourcePlateInfoState,
} from "./state-helpers";

export interface NucleofectionToolState {
  sourcePlateInfo: SourcePlateInfoState[];
  compoundPlateInfo: CompoundPlateInfoState[];
  nucPlateInfo: NucleofectionPlateInfoState[];
  intPlateInfo: any[];
  destPlateInfo: DestPlateInfoState[];
  worklistValues: WorklistValuesState;
}

export interface SourcePlateInfoState extends BaseSrcIntDestPlateInfo {
  operatingVol: number;
  wellInfo: any[];
}

export interface CompoundPlateInfoState extends BaseSrcIntDestPlateInfo {
  index: number;
  operatingVol: number;
  wellInfo: any[];
  transferVol: string;
}

export interface NucleofectionPlateInfoState extends BaseSrcIntDestPlateInfo {
  index: number;
  operatingVol: number;
  minOperatingVol: number;
  wellInfo: any[];
  numberOfWashes: number;
  topUpVol: number;
  washVol: number;
}

type NucPlateKeys = keyof NucleofectionPlateInfoState;
export type NucPlateStringKeys = Extract<NucPlateKeys, string>;

export interface IntermediatePlateInfoState extends BaseSrcIntDestPlateInfo {
  operatingVol: number;
}

export interface DestPlateInfoState extends BaseSrcIntDestPlateInfo {
  index: number;
  operatingVol: number;
  minOperatingVol: number;
  preprocess: boolean;
  topup: boolean;
}

type DestPlateKeys = keyof DestPlateInfoState;
export type DestPlateStringKeys = Extract<DestPlateKeys, string>;

export interface WorklistWellMappingState {
  sourcePlateIndex: number;
  sourceWellId: string;
  destPlateIndex: number;
  destWellId: string;
  transferVol?: string;
}

export interface WorklistValuesState {
  sourceToNuc: WorklistWellMappingState[];
  nucToInt: WorklistWellMappingState[];
  intToDest: WorklistWellMappingState[];
}

export interface NucleofectionDestPlateLabwareTypeInfo extends LabwareTypeInfo {
  topup?: boolean;
  preprocess?: boolean;
}

export const NucleofectionInitialToolState: NucleofectionToolState = {
  sourcePlateInfo: Array.from({ length: 1 }, () => getSourcePlateInfoState()),
  compoundPlateInfo: Array.from({ length: 1 }, () =>
    getCompoundPlateInfoState()
  ),
  nucPlateInfo: Array.from({ length: 1 }, () =>
    getNucleofectionPlateInfoState()
  ),
  intPlateInfo: Array.from({ length: 1 }, () =>
    getIntermediatePlateInfoState()
  ),
  destPlateInfo: [
    {
      index: 0,
      plateBarcode: "",
      labwareTypeCode: "",
      operatingVol: 0,
      minOperatingVol: 0,
      rows: [],
      cols: [],
      preprocess: true,
      topup: true,
    },
  ],
  worklistValues: {
    sourceToNuc: [],
    nucToInt: [],
    intToDest: [],
  },
};

export const NucleofectionToolStoreSlice = createSlice({
  name: "nucleofection-tool-info",
  initialState: NucleofectionInitialToolState,
  reducers: {
    RESET_STATE: (state) => {
      state.sourcePlateInfo = NucleofectionInitialToolState.sourcePlateInfo;
      state.compoundPlateInfo = NucleofectionInitialToolState.compoundPlateInfo;
      state.nucPlateInfo = NucleofectionInitialToolState.nucPlateInfo;
      state.intPlateInfo = NucleofectionInitialToolState.intPlateInfo;
      state.destPlateInfo = NucleofectionInitialToolState.destPlateInfo;
      state.worklistValues = NucleofectionInitialToolState.worklistValues;
    },
    UPLOAD_SOURCE_PLATE: (state, action) => {
      state.sourcePlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.sourcePlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.sourcePlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.sourcePlateInfo[action.payload.index].rows = action.payload.rows;
      state.sourcePlateInfo[action.payload.index].cols = action.payload.cols;
      state.sourcePlateInfo[action.payload.index].wellInfo =
        action.payload.wellInfo;
    },
    UPLOAD_COMPOUND_PLATE: (state, action) => {
      state.compoundPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.compoundPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.compoundPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.compoundPlateInfo[action.payload.index].rows = action.payload.rows;
      state.compoundPlateInfo[action.payload.index].cols = action.payload.cols;
      state.compoundPlateInfo[action.payload.index].wellInfo =
        state.sourcePlateInfo[action.payload.index].wellInfo;
      state.compoundPlateInfo[action.payload.index].transferVol =
        action.payload.transferVol;
    },
    UPLOAD_NUCLEOFECTION_PLATE: (state, action) => {
      state.nucPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.nucPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.nucPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.nucPlateInfo[action.payload.index].minOperatingVol =
        action.payload.minOperatingVol;
      state.nucPlateInfo[action.payload.index].rows = action.payload.rows;
      state.nucPlateInfo[action.payload.index].cols = action.payload.cols;
      state.nucPlateInfo[action.payload.index].wellInfo =
        action.payload.wellInfo;
    },
    CLEAR_SOURCE_PLATE: (state, action) => {
      state.sourcePlateInfo[action.payload.index].plateBarcode = "";
      state.sourcePlateInfo[action.payload.index].labwareTypeCode = "";
      state.sourcePlateInfo[action.payload.index].operatingVol = 0;
      state.sourcePlateInfo[action.payload.index].rows = [];
      state.sourcePlateInfo[action.payload.index].cols = [];
      state.sourcePlateInfo[action.payload.index].wellInfo = [];
      state.compoundPlateInfo[action.payload.index].wellInfo = [];
      if (state.worklistValues.sourceToNuc.length > 0) {
        // removes sourceToNuc mappings from nucPlateInfo wellInfo, added for coloring purposes
        state.nucPlateInfo[action.payload.index].wellInfo = state.nucPlateInfo[
          action.payload.index
        ].wellInfo.slice(
          0,
          state.nucPlateInfo[action.payload.index].wellInfo.length -
            state.worklistValues.sourceToNuc.length
        );
      }
      state.worklistValues.sourceToNuc =
        state.worklistValues.sourceToNuc.filter(
          (e) => e.sourcePlateIndex !== action.payload.index
        );
    },
    CLEAR_COMPOUND_PLATE: (state, action) => {
      state.compoundPlateInfo[action.payload.index].plateBarcode = "";
      state.compoundPlateInfo[action.payload.index].labwareTypeCode = "";
      state.compoundPlateInfo[action.payload.index].operatingVol = 0;
      state.compoundPlateInfo[action.payload.index].rows = [];
      state.compoundPlateInfo[action.payload.index].cols = [];
      state.compoundPlateInfo[action.payload.index].wellInfo = [];
      state.compoundPlateInfo[action.payload.index].transferVol = "25";
    },
    CLEAR_NUCLEOFECTION_PLATE: (state, action) => {
      state.nucPlateInfo[action.payload.index].plateBarcode = "";
      state.nucPlateInfo[action.payload.index].labwareTypeCode = "";
      state.nucPlateInfo[action.payload.index].operatingVol = 0;
      state.nucPlateInfo[action.payload.index].minOperatingVol = 0;
      state.nucPlateInfo[action.payload.index].topUpVol = 160;
      state.nucPlateInfo[action.payload.index].rows = [];
      state.nucPlateInfo[action.payload.index].cols = [];
      state.nucPlateInfo[action.payload.index].wellInfo = [];
      state.nucPlateInfo[action.payload.index].numberOfWashes = 0;
      state.nucPlateInfo[action.payload.index].washVol = 0;
      state.worklistValues.sourceToNuc =
        state.worklistValues.sourceToNuc.filter(
          (e) => e.sourcePlateIndex !== action.payload.index
        );
      state.worklistValues.nucToInt = [];
      state.worklistValues.intToDest = [];
    },
    CLEAR_INTERMEDIATE_PLATE: (state, action) => {
      state.intPlateInfo[action.payload.index].plateBarcode = "";
      state.intPlateInfo[action.payload.index].labwareTypeCode = "";
      state.intPlateInfo[action.payload.index].operatingVol = 0;
      state.intPlateInfo[action.payload.index].rows = [];
      state.intPlateInfo[action.payload.index].cols = [];
      state.worklistValues.nucToInt = state.worklistValues.nucToInt.filter(
        (e) => e.sourcePlateIndex !== action.payload.index
      );
      state.worklistValues.intToDest = state.worklistValues.intToDest.filter(
        (e) => e.sourcePlateIndex !== action.payload.index
      );
    },
    SET_NUCLEOFECTION_PLATE: (state, action) => {
      state.nucPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.nucPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.nucPlateInfo[action.payload.index].minOperatingVol =
        action.payload.minOperatingVol;
      state.nucPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.nucPlateInfo[action.payload.index].rows = action.payload.rows;
      state.nucPlateInfo[action.payload.index].cols = action.payload.cols;
      state.nucPlateInfo[action.payload.index].wellInfo = [];
    },
    SRC_TO_NUC_ADDED: (state, action) => {
      state.worklistValues.sourceToNuc.push(...action.payload.selections);
      state.nucPlateInfo[0].wellInfo.push(...action.payload.wellSelections);
    },
    UPDATE_INT_PLATE_INFO: (state, action) => {
      state.intPlateInfo[action.payload.index][action.payload.key] =
        action.payload.value;
    },
    SET_INTERMEDIATE_PLATE: (state, action) => {
      state.intPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.intPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.intPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.intPlateInfo[action.payload.index].rows = action.payload.rows;
      state.intPlateInfo[action.payload.index].cols = action.payload.cols;
    },
    SET_COMPOUND_PLATE: (state, action) => {
      state.compoundPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.compoundPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.compoundPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.compoundPlateInfo[action.payload.index].rows = action.payload.rows;
      state.compoundPlateInfo[action.payload.index].cols = action.payload.cols;
      state.compoundPlateInfo[action.payload.index].wellInfo =
        state.sourcePlateInfo[action.payload.index].wellInfo;
    },
    NUC_TO_INT_ADDED: (state, action) => {
      state.worklistValues.nucToInt.push(...action.payload.selections);
    },
    UPDATE_NUC_NUM_WASHES: (state, action) => {
      state.nucPlateInfo[action.payload.index].numberOfWashes =
        action.payload.numberOfWashes;
    },

    UPDATE_NUC_WASHVOL: (state, action) => {
      state.nucPlateInfo[0].washVol = action.payload.washVol;
    },
    UPDATE_SINGLE_DEST_WELL_TRANSFERVOL: (state, action) => {
      const intToDest: WorklistValues[] = [];
      for (const item of action.payload.rows) {
        intToDest.push(item);
      }
      //TODO: Refactor to set single value across all worklist genrators
      //Replace with full array including changed transVol
      state.worklistValues.intToDest = intToDest;
    },
    UPDATE_ALL_DEST_WELLS_TRANSFERVOL: (state, action) => {
      const intToDest = state.worklistValues.intToDest.map((e) => ({
        ...e,
        transferVol: action.payload.transferVol,
      }));

      state.worklistValues.intToDest = intToDest;
    },
    UPDATE_EMPTY_DEST_WELLS_TRANSFERVOL: (state, action) => {
      state.worklistValues.intToDest = state.worklistValues.intToDest.map(
        (wellMapping) =>
          wellMapping.transferVol === ""
            ? { ...wellMapping, transferVol: action.payload.transferVol }
            : wellMapping
      );
    },
    UPDATE_WELLS_IN_SPECIFIC_DESTPLATE_TRANSFERVOL: (state, action) => {
      state.worklistValues.intToDest = state.worklistValues.intToDest.map(
        (wellMapping) =>
          wellMapping.destPlateIndex === action.payload.destPlateIndex
            ? { ...wellMapping, transferVol: action.payload.transferVol }
            : wellMapping
      );
    },
    UPDATE_MULTIPLE_DESTPLATE_TRANSFERVOL: (state, action) => {
      const destPlateVolDict = action.payload.destPlateVolDict;
      const updatedIntToDest = state.worklistValues.intToDest.map(
        (wellMapping) => {
          if (wellMapping.destPlateIndex in destPlateVolDict) {
            return {
              ...wellMapping,
              transferVol: destPlateVolDict[wellMapping.destPlateIndex],
            };
          }
          return wellMapping;
        }
      );

      state.worklistValues.intToDest = updatedIntToDest;
    },
    ADD_DESTINATION_PLATE: (state, action) => {
      const lastIndex =
        state.destPlateInfo.length > 0
          ? state.destPlateInfo[state.destPlateInfo.length - 1].index
          : -1;
      state.destPlateInfo.push({
        index: lastIndex + 1,
        plateBarcode: "",
        labwareTypeCode: "",
        operatingVol: 0,
        minOperatingVol: 0,
        rows: [],
        cols: [],
        preprocess: true,
        topup: true,
      });
    },
    SET_DESTINATION_PLATE: (state, action) => {
      state.destPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
      state.destPlateInfo[action.payload.index].operatingVol =
        action.payload.operatingVol;
      state.destPlateInfo[action.payload.index].minOperatingVol =
        action.payload.minOperatingVol;
      state.destPlateInfo[action.payload.index].labwareTypeCode =
        action.payload.labwareTypeCode;
      state.destPlateInfo[action.payload.index].rows = action.payload.rows;
      state.destPlateInfo[action.payload.index].cols = action.payload.cols;
      state.destPlateInfo[action.payload.index].topup = action.payload.topup;
      state.destPlateInfo[action.payload.index].preprocess =
        action.payload.preprocess;
    },
    UPDATE_DEST_PLATE_INFO: (state, action) => {
      const index = action.payload.index;
      const key = action.payload.key as DestPlateStringKeys;
      const value = action.payload.value;
      if (typeof state.destPlateInfo[index][key] == typeof value) {
        //@ts-ignore
        state.destPlateInfo[index][key] = value;
      }
    },
    CLEAR_DESTINATION_PLATE: (state, action) => {
      state.destPlateInfo[action.payload.index].plateBarcode = "";
      state.destPlateInfo[action.payload.index].operatingVol = 0;
      state.destPlateInfo[action.payload.index].labwareTypeCode = "";
      state.destPlateInfo[action.payload.index].operatingVol = 0;
      state.destPlateInfo[action.payload.index].minOperatingVol = 0;
      state.destPlateInfo[action.payload.index].rows = [];
      state.destPlateInfo[action.payload.index].cols = [];
      state.destPlateInfo[action.payload.index].preprocess = true;
      state.destPlateInfo[action.payload.index].topup = true;
      state.worklistValues.intToDest = state.worklistValues.intToDest.filter(
        (e) => e.destPlateIndex !== action.payload.index
      );
      if (state.destPlateInfo.length > 1) {
        const removedIndex = action.payload.index;

        state.destPlateInfo.splice(removedIndex, 1);

        state.worklistValues.intToDest = state.worklistValues.intToDest.map(
          (destWell) => {
            if (destWell.destPlateIndex > removedIndex) {
              destWell.destPlateIndex -= 1;
            }
            return destWell;
          }
        );

        state.destPlateInfo.map((destplate) => {
          if (destplate.index > removedIndex) {
            destplate.index -= 1;
          }
          return destplate;
        });
      }
    },
    UPDATE_INT_PLATEBARCODE: (state, action) => {
      if (state.intPlateInfo[0].labwareTypeCode !== "") {
        state.intPlateInfo[0].plateBarcode = action.payload.intPlateBarcode;
      }
    },
    UPDATE_DEST_PLATEBARCODE: (state, action) => {
      state.destPlateInfo[action.payload.index].plateBarcode =
        action.payload.plateBarcode;
    },
    UPDATE_NUC_PLATE_INFO: (state, action) => {
      const index = action.payload.index;
      const key = action.payload.key as NucPlateStringKeys;
      const value = action.payload.value;
      if (typeof state.nucPlateInfo[index][key] == typeof value) {
        //@ts-ignore
        state.nucPlateInfo[index][key] = value;
      }
    },
    ADD_RESERVED_NUC_WELLS: (state, action) => {
      state.nucPlateInfo[0].wellInfo.push(...action.payload.newWellInfo);
    },
    INT_TO_DEST_ADDED: (state, action) => {
      state.worklistValues.intToDest.push(...action.payload.selections);
    },
    UPLOAD_WORKLIST_VALUES: (state, action) => {
      const properties: string[] = ["sourceToNuc", "nucToInt", "intToDest"];
      properties.forEach((property) => {
        const value = Reflect.get(action.payload, property);
        Reflect.set(state.worklistValues, property, value);
      });
    },
    UPLOAD_SOURCE_CPD_NUC_INT_DEST_PLATE_INFO: (state, action) => {
      state.sourcePlateInfo = action.payload.sourcePlateInfo;
      state.compoundPlateInfo = action.payload.compoundPlateInfo;
      state.nucPlateInfo = action.payload.nucPlateInfo;
      state.intPlateInfo = action.payload.intPlateInfo;
      state.destPlateInfo = action.payload.destPlateInfo;
    },
  },
});

export const NucleofectionToolReducer = NucleofectionToolStoreSlice.reducer;

export const NucleofectionToolActions = NucleofectionToolStoreSlice.actions;

export const NucleofectionTool = undoable(NucleofectionToolReducer, {
  filter: excludeAction([
    NucleofectionToolActions.UPDATE_DEST_PLATE_INFO.toString(),
  ]),
});
